import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DocumentTitle, usePage, usePageIcon } from "../../routes";

import { Dialog } from "primereact/dialog";

import { CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";

import { Instrument, Measurement, MeasurementDetail, Result } from "../shared/interfaces/Result";
import { MessageCard, MessageType } from "../shared/MessageCard";

import { useQueryClient } from "react-query";
import { useAssetById } from "../data/Assets/useAssetById";
import { useAddReportingMeasurements } from "../data/useAddReportingMeasurements";
import { useGetReportingMeasurementsByResultCapture } from "../data/useGetReportingMeasurementsByResultCapture";
import { useRemoveReportingMeasurements } from "../data/useRemoveReportingMeasurements";
import { getAssetTypeLabel, managedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { MeasurementComponent, MeasurementType, SubLocation } from "../shared/enums/Result";
import { ReportingMeasurement } from "../shared/interfaces/ReportingMeasurement";
import { Snackbar } from "../shared/Snackbar";
import { MeasurementDetails, NumericalTabsList } from "./MeasurementDetails";
import { ResultViewer } from "./ResultViewer";
import { storeMeasurementInSession } from "./utils/storeMeasurementInSession";

interface Props {
  measurementDetail: MeasurementDetail<Measurement>;
  selectedResult?: Result;
  measurementHistory?: MeasurementDetail<Measurement>[];
  measurementHistoryWithLatest?: MeasurementDetail<Measurement>[];
  open: boolean;
  instrument: Instrument;
  onClose: () => void;
  onChange: () => void;
  isPartialResult?: boolean;
  isLoading: boolean;
  position?: number;
  resultType?: string;
  customerId?: string;
  companyId?: string;
  siteId?: string;
  isExpertOrGreater?: boolean;
  isEaEmployee?: boolean;
  reportingMeasurements?: ReportingMeasurement[];
  selectedAssetType: string;
  onCheckboxChange?: (
    isChecked: boolean,
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
    chartIndex?: number,
  ) => void;
  onClearSelectedCharts?: (
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
  ) => void;
}

export function MeasurementDialog(props: Props) {
  const { t } = useTranslation();
  const createMeasurementPageURL = useMeasurementPageURLFactory();

  //get the captureID and switchgear assetID from the URL
  const { captureId, switchgearAssetId } = useParams<{
    captureId: string;
    switchgearAssetId: string;
  }>();

  const {
    measurementDetail,
    selectedResult,
    measurementHistory,
    measurementHistoryWithLatest,
    instrument,
    open,
    onClose,
    onChange,
    isPartialResult,
    position,
    resultType,
    customerId,
    companyId,
    siteId,
    isExpertOrGreater,
    isEaEmployee,
    reportingMeasurements,
    onCheckboxChange,
    onClearSelectedCharts,
  } = props;

  const icons = (
    <Tooltip title={<div>{t("common.openInNewTab")}</div>}>
      <IconButton
        component={Link}
        className="p-dialog-header-icon p-link"
        target="_blank"
        rel="opener"
        to={{
          pathname: createMeasurementPageURL(
            resultType,
            captureId,
            switchgearAssetId,
            measurementDetail?.measurement,
            customerId,
            companyId,
            siteId,
          ),
        }}
        onClick={() => {
          storeMeasurementInSession({
            measurementDetail,
            measurementHistory,
            instrument,
            selectedResult,
            isPartialResult,
            position,
            isExpertOrGreater,
            isEaEmployee,
          });

          onClose();
        }}
        size="large"
      >
        <span className="pi pi-external-link"></span>
      </IconButton>
    </Tooltip>
  );

  const header = (
    <Typography variant="h6" component="p">
      {`${t("results.measurementType", { measurementType: `$t(MeasurementTypes.${measurementDetail.measurementType} )` })} - ${t("common.formattedDate", { dateString: selectedResult?.time })}`}
    </Typography>
  );

  return (
    <Dialog
      icons={icons}
      header={header}
      headerStyle={{ padding: "10px 20px" }}
      visible={open}
      onHide={onClose}
      maximized
      draggable={false}
    >
      {props.isLoading ? (
        <div className="d-flex justify-content-center">
          <CircularProgress size={65} />
        </div>
      ) : (
        <>
          <MeasurementDetails
            measurementDetail={measurementDetail}
            selectedResult={selectedResult}
            measurementHistory={measurementHistory}
            measurementHistoryWithLatest={measurementHistoryWithLatest}
            customerId={Number(customerId)}
            captureId={Number(captureId)}
            instrument={instrument}
            isPartialResult={isPartialResult}
            onChange={onChange}
            position={props.position}
            onCheckboxChange={onCheckboxChange}
            isExpertOrGreater={isExpertOrGreater}
            isEaEmployee={isEaEmployee}
            reportingMeasurements={reportingMeasurements}
            resultType={resultType}
            onClearSelectedCharts={onClearSelectedCharts}
            selectedAssetType={props.selectedAssetType}
          />
        </>
      )}
    </Dialog>
  );
}

export function useMeasurementPageURLFactory() {
  const measurementViewerPath = usePage(MeasurementPage.name)?.path;

  return (
    resultType?: string,
    captureId?: string,
    switchgearAssetId?: string,
    measurement?: Measurement,
    customerId?: string,
    companyId?: string,
    siteId?: string,
  ) =>
    measurementViewerPath
      ?.replace(":resultType", resultType || "")
      .replace(":captureId", captureId || "")
      .replace(":switchgearAssetId", switchgearAssetId || "")
      .replace(":componentAssetId", measurement?.ComponentAssetId.toString() || "")
      .replace(":measurementType", measurement?.MeasurementType || "")
      .replace(":customerId", customerId || "")
      .replace(":companyId", companyId || "")
      .replace(":siteId", siteId || "");
}

export function MeasurementPage() {
  const { t } = useTranslation();
  const pageIcon = usePageIcon(MeasurementPage.name);
  const resultViewerPath = usePage(ResultViewer.name)?.path;
  const navigate = useNavigate();

  const { resultType, captureId, switchgearAssetId, componentAssetId, measurementType, customerId, companyId, siteId } =
    useParams<{
      resultType: string;
      captureId: string;
      switchgearAssetId: string;
      componentAssetId?: string;
      measurementType?: string;
      customerId: string;
      companyId: string;
      siteId: string;
    }>();

  const sessionMeasurement = sessionStorage.getItem(`measurement-${componentAssetId}-${measurementType}`);
  const locationProps = sessionMeasurement && (JSON.parse(sessionMeasurement) as Props);

  const {
    measurementDetail,
    selectedResult,
    measurementHistory,
    instrument,
    isPartialResult,
    position,
    isExpertOrGreater,
    isEaEmployee,
  } = locationProps || {};

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const addReportingMeasurements = useAddReportingMeasurements({
    onSuccess: handleAddReportingMeasurementsSuccess,
    onError: handleAddReportingMeasurementsError,
  });
  const removeReportingMeasurements = useRemoveReportingMeasurements({
    onSuccess: handleRemoveReportingMeasurementsSuccess,
    onError: handleRemoveReportingMeasurementsError,
  });

  const reportingMeasurementsByResultCapture = useGetReportingMeasurementsByResultCapture(
    Number(captureId),
    Number(customerId),
    Number(switchgearAssetId),
  );
  const queryClient = useQueryClient();

  function handleAddReportingMeasurementsSuccess() {
    queryClient.invalidateQueries({ queryKey: ["reportingMeasurements"] });
  }

  function handleRemoveReportingMeasurementsSuccess() {
    queryClient.invalidateQueries({ queryKey: ["reportingMeasurements"] });
  }

  function handleAddReportingMeasurementsError() {
    setSnackbarText(t("results.addReportingMeasurementError"));
    setShowSnackbar(true);
  }

  function handleRemoveReportingMeasurementsError() {
    setSnackbarText(t("results.removeReportingMeasurementError"));
    setShowSnackbar(true);
  }

  function handleChange() {
    resultViewerPath &&
      navigate(
        resultViewerPath
          ?.replace(":resultType", resultType || "")
          .replace(":captureId", captureId || "")
          .replace(":switchgearAssetId", switchgearAssetId || "")
          .replace(":customerId", customerId || "")
          .replace(":companyId", companyId || "")
          .replace(":siteId", siteId || "") || "",
      );
  }

  function handleCheckboxChange(
    isChecked: boolean,
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
    chartIndex?: number | null,
  ) {
    if (isChecked) {
      const measurementData = {
        customerId: Number(customerId),
        assetId: Number(switchgearAssetId),
        chartCaptureId: captureId,
        resultCaptureId: resultCaptureId,
        chartDataPointId: dataPointId,
        chartTime: time,
        tabPosition,
        componentType,
        measurementType,
        subLocation: subLocation ?? SubLocation.None,
        panel360AssetId,
        panelNo,
        panelName,
        chartIndex,
      };
      addReportingMeasurements.mutate(measurementData);
    } else {
      const measurementData = {
        customerId: Number(customerId),
        assetId: Number(switchgearAssetId),
        chartCaptureId: captureId,
        resultCaptureId: resultCaptureId,
        chartDataPointId: dataPointId,
        chartTime: time,
        tabPosition,
        componentType,
        measurementType,
        subLocation: subLocation ?? SubLocation.None,
        panel360AssetId,
        panelNo,
        panelName,
        chartIndex,
      };
      if (measurementData.chartIndex === undefined) {
        measurementData.chartIndex = null;
      }
      const measurementDataToDelete = reportingMeasurementsByResultCapture.data?.find(
        (measurement: ReportingMeasurement) =>
          measurement.chartDataPointId === measurementData.chartDataPointId &&
          measurement.chartCaptureId === measurementData.chartCaptureId &&
          measurement.resultCaptureId === measurementData.resultCaptureId &&
          measurement.chartTime === measurementData.chartTime &&
          measurement.tabPosition === measurementData.tabPosition &&
          measurement.componentType === measurementData.componentType &&
          measurement.measurementType === measurementData.measurementType &&
          (measurement.subLocation ?? SubLocation.None) === (measurementData.subLocation ?? SubLocation.None) &&
          measurement.panel360AssetId === measurementData.panel360AssetId &&
          measurement.panelNo === measurementData.panelNo &&
          measurement.panelName === measurementData.panelName &&
          measurement.chartIndex === measurementData.chartIndex,
      );
      if (measurementDataToDelete) {
        removeReportingMeasurements.mutate(measurementDataToDelete);
      }
    }
  }

  function handleClearSelectedCharts(
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
  ) {
    const measurementsToDelete = reportingMeasurementsByResultCapture.data?.filter(
      (measurement: ReportingMeasurement) =>
        measurement.chartCaptureId === captureId &&
        measurement.resultCaptureId === resultCaptureId &&
        measurement.chartDataPointId === dataPointId &&
        measurement.chartTime === time &&
        measurement.tabPosition === tabPosition &&
        measurement.componentType === componentType &&
        measurement.measurementType === measurementType &&
        (measurement.subLocation ?? SubLocation.None) === (subLocation ?? SubLocation.None) &&
        measurement.panel360AssetId === panel360AssetId &&
        measurement.panelNo === panelNo &&
        measurement.panelName === panelName,
    );

    if (measurementsToDelete)
      measurementsToDelete.forEach((measurement: ReportingMeasurement) => {
        removeReportingMeasurements.mutate(measurement);
      });
  }

  const assetId = switchgearAssetId !== undefined ? Number(switchgearAssetId) : 0;
  const { data: selectedAsset } = useAssetById(assetId);

  if (measurementDetail && instrument) {
    const pageTitle = `${t("results.measurementType", { measurementType: `$t(MeasurementTypes.${measurementDetail.measurementType} )` })} - ${t("common.formattedDate", { dateString: selectedResult?.time })}`;
    return (
      <React.Fragment>
        <DocumentTitle title={pageTitle} />
        <div className="pl-3 pb-3">
          <Typography variant="h6" component="p">
            {pageIcon} {pageTitle}
          </Typography>
        </div>
        <MeasurementDetails
          measurementDetail={measurementDetail}
          selectedResult={selectedResult}
          measurementHistory={measurementHistory}
          customerId={Number(customerId)}
          captureId={Number(captureId)}
          instrument={instrument}
          isPartialResult={isPartialResult}
          onChange={handleChange}
          position={position}
          onCheckboxChange={handleCheckboxChange}
          isExpertOrGreater={isExpertOrGreater}
          isEaEmployee={isEaEmployee}
          reportingMeasurements={reportingMeasurementsByResultCapture.data}
          resultType={resultType}
          onClearSelectedCharts={handleClearSelectedCharts}
          selectedAssetType={getAssetTypeLabel(selectedAsset?.assetTypeKey) || managedSurveyAssetType.Switchgear}
        />
        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />
      </React.Fragment>
    );
  } else
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageCard
          title={t("common.unableToLoadError", {
            val: "$t(results.measurement)",
          })}
          secondaryText={
            <Trans
              i18nKey="common.returnToPage"
              values={{ val: "$t(results.result)" }}
              components={{
                lnk: (
                  <Link
                    to={
                      resultViewerPath
                        ?.replace(":captureId", captureId || "")
                        .replace(":switchgearAssetId", switchgearAssetId || "") || ""
                    }
                  ></Link>
                ),
              }}
            />
          }
          messageType={MessageType.Warning}
        />
      </div>
    );
}
